import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { useServerSideFacetExpansion } from '@/pages/Search/SearchPage/hooks/useServerSideFacetExpansion';
import { CurrentOpenMenu, STANDARD_VIEW_MORE_SIZE } from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getMergedOptionFacet, getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FilterAction } from '@/types/SearchFacet';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';
import {
    isFacetOptionCheckedForSemanticUrl,
    isFlatFacetOptionCheckedForCategoryIdUrl,
} from '@/utils/search/searchQuery';
import { urlSafeTitle } from '@/utils/urls';

import { FacetCard } from '../FacetCard/FacetCard';

export const StyleAndPeriodFilter = () => {
    const { formatMessage } = useIntl();
    const { isLoading, onViewMoreClick } = useServerSideFacetExpansion({
        facetId: FacetTypes.STYLE_PERIOD_ID,
        size: STANDARD_VIEW_MORE_SIZE,
    });
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const styleFacet = useAppSelector((state) => getMergedOptionFacet(state, FacetTypes.STYLE_PERIOD_ID));
    const rawFacets = useAppSelector(getSearchFacets);
    const { options = [] } = styleFacet;

    const isOptionChecked = useCallback(
        (option: FacetOption) => {
            const checkedFromLegacyCategoryId = isFlatFacetOptionCheckedForCategoryIdUrl(
                queryParams as Record<string, unknown>,
                option.id,
                FacetTypes.STYLE_PERIOD_ID,
                rawFacets,
                option.selected
            );
            return isFacetOptionCheckedForSemanticUrl(
                queryParams as Record<string, unknown>,
                FacetTypes.STYLE_PERIOD_ID,
                option,
                checkedFromLegacyCategoryId
            );
        },
        [queryParams, rawFacets]
    );

    const onStyleOptionClick = (option: FacetOption) => {
        const newCheckStatus = !isOptionChecked(option);
        const semanticValue = urlSafeTitle(option.name ?? String(option.id));

        let newParams = modifyParamsWhenChecked(
            queryParams,
            FacetTypes.STYLE_PERIOD_ID,
            semanticValue,
            newCheckStatus,
            {
                useSemanticAttributeParams: true,
            }
        );

        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            browseFacetOptionRemovedId: newCheckStatus ? undefined : option.id,
            currentOpenMenu: CurrentOpenMenu.AllFilters,
            filterAction: newCheckStatus ? FilterAction.StylePeriodChanged : FilterAction.StylePeriodRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const filteredOptions = useMemo(
        () =>
            options
                .filter((option) => option.count !== 0 || isOptionChecked(option))
                .sort((firstOption, secondOption) => secondOption.count - firstOption.count),
        [options, isOptionChecked]
    );

    if (filteredOptions.length === 0) {
        return null;
    }

    return (
        <FacetCard
            headline={formatMessage({ id: 'search_facet_title_stylePeriod' })}
            isLoading={isLoading}
            onViewMoreClick={onViewMoreClick}
        >
            {filteredOptions.map((option) => (
                <Checkbox.Option
                    checked={isOptionChecked(option)}
                    disabled={option.count === 0 && !isOptionChecked(option)}
                    key={option.id}
                    onChange={() => onStyleOptionClick(option)}
                >
                    {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                </Checkbox.Option>
            ))}
        </FacetCard>
    );
};
